<script setup lang="ts">
import { getGFALFeeFromTotalPurchasePrice } from "~/utils/number.utils";
import { PurchaseType } from "~~/shared/types/analyticspurchase.enums";
const emit = defineEmits(["close"]);
const props = defineProps({
  offer: {
    type: Object as PropType<Offer>,
    required: true,
  },
  action: {
    type: String as PropType<"accept" | "reject" | "revoke">,
    required: true,
  },
});

const actionTitle = {
  accept: {
    title: "main.modalOffer.acceptTitle",
    successTitle: "main.modalOffer.acceptSuccess",
    successBody: "main.modalOffer.acceptSuccessDetail",
  },
  reject: {
    title: "main.modalOffer.rejectTitle",
    successTitle: "main.modalOffer.rejectSuccess",
    successBody: "main.modalOffer.rejectSuccessDetail",
  },
  revoke: {
    title: "main.modalOffer.revokeTitle",
    successTitle: "main.modalOffer.revokeSuccess",
    successBody: "main.modalOffer.revokeSuccessDetail",
  },
};

const { acceptOfferOnCollectible, cancelOfferOnCollectible } =
  useCollectiblesController();
const { refreshUserFinancials } = useBalanceRefresh();
const { trackEvent } = useGAnalytics();
const router = useRouter();
const isHandlingOffer = ref<boolean>(true);
const isModalLoaderShown = ref<boolean>(false);
const isModalEndShown = ref<boolean>(false);
const success = ref<boolean>(false);
const isProcessing = ref<boolean>(false);
const errorMessage = ref<string>("");
const { t } = useI18n();
const GFALExStore = useGFALExStore();

const trackOfferAcceptCompleted = () => {
  const itemName =
    t(`${props.offer.translationId}.title`) +
    " " +
    t(`${props.offer.translationId}.subtitle`);
  const itemPriceGFAL = getGFALFeeFromTotalPurchasePrice(
    Number(props.offer.amount.GFAL)
  );
  const itemPriceUSD = itemPriceGFAL * GFALExStore.GFALExRate;
  const purchasePayload = {
    transaction_id: props.offer.id,
    currency: "USD",
    value: itemPriceUSD,
    purchase_type: PurchaseType.PLAYERS_MARKET_OFFER_FEE,
    items: [
      {
        item_id: `${props.offer.collectibleId}`,
        item_name: itemName,
        item_category: `collectible`,
        price: itemPriceUSD,
        quantity: 1,
      },
    ],
  };
  trackEvent("purchase", purchasePayload);
};

const acceptOffer = async () => {
  trackEvent("accept_offer_confirm");
  isHandlingOffer.value = false;
  isModalLoaderShown.value = true;
  try {
    const res = await acceptOfferOnCollectible(
      props.offer.collectibleId,
      props.offer.id
    );
    if (!res) {
      throw new Error("Failed to accept offer");
    }
    ///// HANDLE OFFER ACCEPTED TRACK EVENT PURCHASE FEE FROM GFALID OF TTHE BUYER NOT SELLER!
    if (res.statusCode === 200) {
      refreshUserFinancials();
      success.value = true;
      trackEvent("accept_offer_success");
    } else if (res.statusCode === 202) {
      trackEvent("accept_offer_pending");
      isProcessing.value = true;
    }
    trackOfferAcceptCompleted();
  } catch (error: any) {
    trackEvent("accept_offer_error");
    switch (error.statusCode) {
      case 402:
        errorMessage.value =
          "errorMessages.userWalletInsufficientFundsOfferBuyer";
        break;
      case 403:
        errorMessage.value = "errorMessages.userWalletNotCreated";
        break;
      case 433:
        errorMessage.value = "errorMessages.offerAcceptedMismatch";
        break;
      case 451:
        errorMessage.value = "errorMessages.userKYCBanned";
        break;
      default:
        errorMessage.value = "errorMessages.default";
        break;
    }
    console.error(error);
  } finally {
    isModalLoaderShown.value = false;
    isModalEndShown.value = true;
  }
};

const rejectOffer = async () => {
  trackEvent("reject_offer_confirm");
  isHandlingOffer.value = false;
  isModalLoaderShown.value = true;
  try {
    const res = await cancelOfferOnCollectible(
      props.offer.collectibleId,
      props.offer.id
    );
    if (!res) {
      throw new Error("Failed to reject offer");
    }
    success.value = true;
    trackEvent("reject_offer_success");
  } catch (error) {
    trackEvent("reject_offer_error");
    console.error(error);
  } finally {
    isModalLoaderShown.value = false;
    isModalEndShown.value = true;
  }
};

const cancelOffer = async () => {
  trackEvent("revoke_offer_confirm");
  isHandlingOffer.value = false;
  isModalLoaderShown.value = true;
  try {
    const res = await cancelOfferOnCollectible(
      props.offer.collectibleId,
      props.offer.id
    );
    if (!res) {
      throw new Error("Failed to revoke offer");
    }
    if (res.statusCode === 200) {
      success.value = true;
      trackEvent("revoke_offer_success");
    } else if (res.statusCode === 202) {
      trackEvent("revoke_offer_pending");
      isProcessing.value = true;
    }
  } catch (error) {
    trackEvent("revoke_offer_error");
    console.error(error);
  } finally {
    isModalLoaderShown.value = false;
    isModalEndShown.value = true;
  }
};

const onCloseEnd = () => {
  if (props.action === "accept") {
    router.push("/user-portal/offers");
  }
  emit("close");
};
</script>
<template>
  <BaseLayoutModal
    width="medium"
    :hide-x="isModalLoaderShown"
    @close="
      isModalLoaderShown
        ? null
        : isModalEndShown
        ? onCloseEnd()
        : $emit('close')
    "
    @mouse-up="isModalLoaderShown ? null : $emit('close')">
    <template #header>
      {{ $t(actionTitle[action].title) }}
    </template>
    <template #content>
      <Transition mode="out-in">
        <ModalOfferHandleAccept
          v-if="isHandlingOffer && action === 'accept'"
          :offer="offer"
          @submit-accept="acceptOffer"
          @close="$emit('close')" />
        <ModalOfferHandleReject
          v-else-if="isHandlingOffer && action === 'reject'"
          :offer="offer"
          @submit-reject="rejectOffer"
          @close="$emit('close')" />
        <ModalOfferHandleRevoke
          v-else-if="isHandlingOffer && action === 'revoke'"
          :offer="offer"
          @submit-revoke="cancelOffer"
          @close="$emit('close')" />
        <ModalLoader
          v-else-if="isModalLoaderShown"
          :title="$t(actionTitle[action].title)"
          :body-title="$t('main.modalLoader.transactionInProgress')"
          :body-subtitle="$t('main.modalLoader.transactionUnderway')" />
        <ModalOfferHandleEnd
          v-else-if="isModalEndShown"
          :success="success"
          :is-processing="isProcessing"
          :error-message="errorMessage"
          :header-text="$t(actionTitle[action].successTitle)"
          :body-text="$t(actionTitle[action].successBody)"
          @close="onCloseEnd" />
      </Transition>
    </template>
  </BaseLayoutModal>
</template>

<style scoped>
.v-enter-active {
  transition: all 0.3s ease-out;
}
.v-enter-from {
  opacity: 0;
  transform: translate(0, 1rem);
}
.v-enter-to {
  opacity: 1;
  transform: translate(0, 0);
}
.v-leave-active {
  transition: all 0.2s ease-in;
}
.v-leave-from {
  opacity: 1;
  transform: translate(0, 0);
}
.v-leave-to {
  opacity: 0;
  transform: translate(0, 1rem);
}
@media (min-width: 640px) {
  .v-enter-from,
  .v-leave-to {
    transform: translate(0, 0) scale(0.95);
  }
  .v-enter-to,
  .v-leave-from {
    transform: translate(0, 0) scale(1);
  }
}
</style>
